import React from "react";
import { useTranslation } from "react-i18next";
import { RiRobot2Line } from "react-icons/ri";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import type { ComponentMessageType } from "../../../types/ComponentMessageType";
import { senderInitials } from "../../../utils/senderInitials";
import styles from "../message.module.scss";

/**
 * Badge shown next to an outbound message identifying who sent it — a human agent
 * (initials circle) or a bot flow (robot icon). Renders nothing when the message
 * carries no sender attribution (legacy messages).
 */
export const SenderAvatar: React.FC<{ sentBy?: ComponentMessageType["sentBy"] }> = ({ sentBy }) => {
  const { t } = useTranslation();
  if (!sentBy) return null;

  if (sentBy.type === "bot") {
    const label = t("chat_component.sent_by_bot", "Bot Flow");
    const versionSuffix = sentBy.botFlowVersion != null ? ` (v${sentBy.botFlowVersion})` : "";
    const idSuffix = sentBy.botFlowId ? ` (ID: ${sentBy.botFlowId})` : "";
    // Omit the "· name" segment entirely when there is no name, to avoid a dangling "Bot Flow: (v3)".
    const tooltip = `${sentBy.name ? `${label}: ${sentBy.name}` : label}${versionSuffix}${idSuffix}`;
    return (
      <Tooltip value={tooltip} placement="left" delay={300}>
        <div className={`${styles.senderAvatar} ${styles.senderAvatarBot}`} data-testid="sender-avatar-bot">
          <RiRobot2Line size={12} />
        </div>
      </Tooltip>
    );
  }

  const label = t("chat_component.sent_by_agent", "Agent");
  const idSuffix = sentBy.userId ? ` (ID: ${sentBy.userId})` : "";
  const tooltip = `${sentBy.name ? `${label}: ${sentBy.name}` : label}${idSuffix}`;
  return (
    <Tooltip value={tooltip} placement="left" delay={300}>
      <div className={`${styles.senderAvatar} ${styles.senderAvatarAgent}`} data-testid="sender-avatar-agent">
        {senderInitials(sentBy.name)}
      </div>
    </Tooltip>
  );
};
